import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Component, Injectable, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Entry } from 'contentful';
import { filter, finalize } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { tap } from 'rxjs/operators';
import { config } from 'src/environments/environment';
import authenticationEndPoints from 'src/assets/json/redirects/authentication-end-points.json';

import { HomePage } from './model/page/home-page';
import { entraAuthService } from './service/auth.service';
import { ContentfulService } from './service/contentful.service';
import { GoogleAnalyticsService } from './service/google-analytics.service';
import { PageLoaderService } from './service/page-loader.service';
import { SandboxPageStateService } from './service/sandbox-page-state.service';

declare const gtag: Function;

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {
  constructor(private entraAuthService: entraAuthService, private pageLoaderService: PageLoaderService, private router: Router, private sandboxPageStateService: SandboxPageStateService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    this.pageLoaderService.addPageLoader(true);
    let accessToken = '';
    if (authenticationEndPoints.some((endpoint) => req.url.includes(endpoint))) {
      const tokenObj = this.sandboxPageStateService.getTokenObject();
      if (tokenObj) {
        accessToken = tokenObj.access_token;
      } else {
        localStorage.setItem('redirect_url', window.location.pathname);
        this.entraAuthService.initLogin();
      }
    }

    const apiConnectionProfile = this.sandboxPageStateService.getActiveAPIConnectionProfile();

    let requestWithHeaders = req.clone({
      setHeaders: {
        SOAPAction: (req.headers && req.headers.get('soapAction')) || '',
        Authorization: accessToken
      }
    });

    if (apiConnectionProfile.clientKey && apiConnectionProfile.clientKey.length > 0) {
      requestWithHeaders = req.clone({
        setHeaders: {
          'Client-Key': apiConnectionProfile.clientKey,
          SOAPAction: (req.headers && req.headers.get('soapAction')) || '',
          Authorization: accessToken
        }
      });
    }

    let responseHeaders: any;

    const response = next.handle(requestWithHeaders).pipe(
      tap((httpEvent: any) => {
        if (httpEvent instanceof HttpResponse) {
          responseHeaders = httpEvent.headers;
        }
      }),
      finalize(() => {
        this.pageLoaderService.addPageLoader(false);
        if (req.url.includes('17.2') || req.url.includes('DisruptBooking') || req.url.includes('19.1')) {
          const httpHeadersMap = new Map();
          httpHeadersMap.set('URL', req.url);
          requestWithHeaders.headers.keys().forEach((key) => httpHeadersMap.set(key, requestWithHeaders.headers.get(key)));
          httpHeadersMap.delete('Client-Key');
          responseHeaders && responseHeaders.keys().forEach((key: any) => httpHeadersMap.set(key, responseHeaders.get(key)));
          this.sandboxPageStateService.updateXMLLogsMapWithHeaders(' { "HttpHeaders" : ' + JSON.stringify(Object.fromEntries(httpHeadersMap)) + ' }');
          this.sandboxPageStateService.updateRequestMap(req.url, req.body);
        }
      })
    );
    return response;
  }
}
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  isSandboxLogViewPage = false;
  loading: boolean = false;
  isRegisterPage = false;
  homePage: HomePage = {} as HomePage;

  constructor(
    private pageLoaderService: PageLoaderService,
    private gaService: GoogleAnalyticsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private contentfulService: ContentfulService,
    private auth: entraAuthService,
    private googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((event) => this.modifyHeader(event));
  }

  ngOnInit(): void {

    const alreadyHandled = sessionStorage.getItem('handledRedirect');
    (window as any).gaTrackingId = config.gaTrackingId;
    (window as any).OptanonWrapper = () => {};
    this.loadOneTrust();
    if (alreadyHandled) {
      sessionStorage.removeItem('handledRedirect');
      const redirectUrl = localStorage.getItem('ndcportal_redirect');


      if (redirectUrl && redirectUrl.includes('/new-project-api')) {
        const redirect_url= new URL(redirectUrl, window.location.origin);
        if (redirectUrl && redirect_url.origin === window.location.origin) {
          localStorage.removeItem('ndcportal_redirect');
          window.location.href = redirect_url.href;
        }else{
          window.location.href = '/';
        }
      }
    }

    const authRedirectUrl = localStorage.getItem('redirect_url');
    if (authRedirectUrl && this.auth.isLoggedIn()) {
      localStorage.removeItem('redirect_url');
      this.router.navigateByUrl(authRedirectUrl);
    }
    this.activatedRoute.queryParams.subscribe((queryParams) => {
      this.isSandboxLogViewPage = queryParams['log'];
    });
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((event) => this.checkSureroutePageName(event));

    this.pageLoaderService.pageLoader.subscribe((status) => {
      setTimeout(() => {
        this.loading = typeof status === 'boolean' ? status : false;
      }, 200);
    });
    if (window.navigator && navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then(function (registrations) {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }
  }

  private loadOneTrust(): void {
    const script = document.createElement('script');
    script.src = config.oneTrust.srcUrl;
    script.setAttribute('data-domain-script', config.oneTrust.dataDomainScript);
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    document.head.appendChild(script);
  }

  private mapHtmlTitle(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      this.homePage.greyOut = entry.fields.greyOut;
    });
  }

  isLoading() {
    return this.loading;
  }
  modifyHeader(location: any) {
    if (location.url === '/home') {
      const query = { 'fields.title': 'HOME', include: 1 };
      this.contentfulService.getPageContentResponse(query).then((homePageEntries) => {
        this.mapHtmlTitle(homePageEntries.items);
      });
    } else if (location.url !== '/home') {
      this.homePage.greyOut = false;
    }
    if (location.url === '/account/register') {
      this.isRegisterPage = true;
    } else {
      this.isRegisterPage = false;
    }
  }
  checkSureroutePageName(location: any) {
    if (location.url === '/akamai/sureroute-test-object.html') {
      this.isSandboxLogViewPage = true;
    }
  }
}
